import { KeychainAccess, SecureStorage } from '@aparajita/capacitor-secure-storage';

import { jsonParse, jsonStringify } from 'utils/json';
import { logger } from 'utils/logger';

class SecureTokenStore {
  #cache = new Map();
  #isHydrated = false;

  hydrate = async keys => {
    try {
      await SecureStorage.setSynchronize(false);
      await SecureStorage.setDefaultKeychainAccess(KeychainAccess.afterFirstUnlockThisDeviceOnly);

      await Promise.all(
        keys.map(async key => {
          const value = await SecureStorage.get(key, false, false);
          this.#cache.set(key, value ?? null);
        }),
      );
    } catch (error) {
      logger.error('[SecureTokenStore] hydrate failed', error);
    } finally {
      this.#isHydrated = true;
    }
  };

  get isHydrated() {
    return this.#isHydrated;
  }

  setItem = (key, value, shouldStringify) => {
    const keyValue = shouldStringify ? jsonStringify(value) : value;
    this.#cache.set(key, keyValue);

    // Return the persist promise so the token-rotation path can AWAIT it. A rotated refresh token must
    // reach SecureStorage before the old one is consumed — otherwise an app kill in the gap replays the
    // already-rotated token on next launch, tripping the backend's refresh-token reuse-detection, which
    // revokes the whole token family and forces a full re-login inside the 30-day window (DEV-4332).
    return SecureStorage.set(
      key,
      keyValue,
      false,
      false,
      KeychainAccess.afterFirstUnlockThisDeviceOnly,
    ).catch(error => logger.error('[SecureTokenStore] persist failed', error));
  };

  getItem = key => {
    return this.#cache.get(key) ?? null;
  };

  getParsedItem = key => {
    const data = this.getItem(key);

    try {
      return jsonParse(data);
    } catch (error) {
      logger.error('[SecureTokenStore] parse failed', error);

      return null;
    }
  };

  removeItem = key => {
    this.#cache.delete(key);

    return SecureStorage.remove(key, false).catch(error =>
      logger.error('[SecureTokenStore] remove failed', error),
    );
  };
}

export default SecureTokenStore;
